import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

function AlbumShare({ user }) {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [shares, setShares] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [albumId]);

  const fetchData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      console.log('[AlbumShare] Fetching album data for:', albumId);

      // Fetch album
      const albumResponse = await axios.get(`${backendUrl}/api/photo-albums/${albumId}`);
      const albumData = albumResponse.data;
      setAlbum(albumData);
      console.log('[AlbumShare] Album loaded:', albumData.name);

      // Fetch shares
      let sharesData = [];
      try {
        const sharesResponse = await axios.get(`${backendUrl}/api/photo-albums/${albumId}/shares`);
        sharesData = sharesResponse.data;
        setShares(sharesData);
        console.log('[AlbumShare] Current shares:', sharesData.length);
      } catch (error) {
        console.log('[AlbumShare] No shares yet or error fetching shares');
        setShares([]);
      }

      // Fetch all users for sharing
      console.log('[AlbumShare] Fetching users list...');
      const usersResponse = await axios.get(`${backendUrl}/api/users`);
      const usersData = usersResponse.data;
      console.log('[AlbumShare] Total users found:', usersData.length);
      console.log('[AlbumShare] First 3 users:', usersData.slice(0, 3).map(u => ({ name: u.name, id: u.id, email: u.email })));
      
      // Filter out album owner and already shared users
      console.log('[AlbumShare] Current user from props:', { name: user?.name, id: user?.id });
      
      if (!user || !user.id) {
        console.error('[AlbumShare] ERROR: User is null or missing ID!');
        toast.error('User session error. Please refresh and try again.');
        return;
      }
      
      const sharedUserIds = sharesData.map(s => s.user_id);
      console.log('[AlbumShare] Already shared with user IDs:', sharedUserIds);
      
      // Apply role-based filtering
      let filteredUsers = usersData.filter(u => u.id !== user.id && !sharedUserIds.includes(u.id));
      
      // If user is employee, only show admins and other employees (no clients)
      if (user.role === 'employee') {
        filteredUsers = filteredUsers.filter(u => 
          u.role === 'admin' || u.role === 'supervisor' || u.role === 'employee'
        );
        console.log('[AlbumShare] Employee user - filtered to exclude clients');
      }
      // Admins can share with everyone (no additional filtering needed)
      
      console.log('[AlbumShare] Available users to share with:', filteredUsers.length);
      console.log('[AlbumShare] User role:', user.role);
      console.log('[AlbumShare] First 3 available users:', filteredUsers.slice(0, 3).map(u => ({ name: u.name, id: u.id, email: u.email, role: u.role })));
      
      const availableUsers = filteredUsers;
      
      setAllUsers(availableUsers);
      console.log('[AlbumShare] State updated with', availableUsers.length, 'users');
    } catch (error) {
      console.error('[AlbumShare] Error fetching data:', error);
      toast.error('Failed to load sharing information: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleShareAlbum = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user to share with');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      console.log('[AlbumShare] Sharing album with user:', selectedUserId);
      
      await axios.post(`${backendUrl}/api/photo-albums/${albumId}/share`, {
        shared_with_user_id: selectedUserId
      });

      toast.success('Album shared successfully!');
      setSelectedUserId('');
      fetchData();
    } catch (error) {
      console.error('[AlbumShare] Error sharing album:', error);
      toast.error('Failed to share album');
    }
  };

  const handleUnshare = async (userId) => {
    if (!window.confirm('Remove this user\'s access to the album?')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      console.log('[AlbumShare] Removing share for user:', userId);
      
      await axios.delete(`${backendUrl}/api/photo-albums/${albumId}/share/${userId}`);

      toast.success('Access removed successfully!');
      fetchData();
    } catch (error) {
      console.error('[AlbumShare] Error unsharing album:', error);
      toast.error('Failed to remove access: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  console.log('[AlbumShare] Rendering with allUsers:', allUsers.length, 'users');
  console.log('[AlbumShare] allUsers state:', allUsers.slice(0, 3));

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 pt-24 sm:pt-8">
      <Button
        size="lg"
        className="mb-6 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 shadow-md hover:shadow-lg transition-all"
        onClick={() => navigate('/photo-albums')}
      >
        <ArrowLeft className="h-5 w-5" />
        Back to Photo Albums
      </Button>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">Share Album</h1>
      <p className="text-gray-600 mb-2">{album?.name}</p>
      
      {/* Role-based sharing notice */}
      {user && user.role === 'employee' && (
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Employees can share with admins and other employees only. 
            To share with clients, please ask an admin.
          </p>
        </div>
      )}
      
      {user && (user.role === 'admin' || user.role === 'supervisor') && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Admin Access:</strong> You can share this album with any user including clients.
          </p>
        </div>
      )}

      {/* Share with New User */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Share with User
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a user" />
              </SelectTrigger>
              <SelectContent>
                {allUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} ({user.email}) - {user.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleShareAlbum}>Share</Button>
          </div>
          {allUsers.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">No more users available to share with</p>
          )}
        </CardContent>
      </Card>

      {/* Current Shares */}
      <Card>
        <CardHeader>
          <CardTitle>Shared With ({shares.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {shares.length === 0 ? (
            <p className="text-gray-500">This album is not shared with anyone yet</p>
          ) : (
            <div className="space-y-3">
              {shares.map((share) => (
                <div
                  key={share.share_id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{share.user_name}</p>
                    <p className="text-sm text-gray-600">{share.user_email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Shared on {new Date(share.shared_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleUnshare(share.user_id)}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AlbumShare;
